/**
 * AgedLeads.jsx — v2 Aged Lead Pool agent view.
 *
 * Tabs: All Available / In Progress / My Locked / My History.
 * Header actions: Invite Teammate · Upload Leads · Power Dial Pool.
 *
 * Power dialing is MANUAL only (no AI screener) — the "Power Dial Pool"
 * button starts a session and routes to /aged-leads/power-dial/:sessionId.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, api } from '../lib/api.js';
import { useAuth } from '../store/auth.js';
import TopBar from '../components/TopBar.jsx';
import LeadDrawer from '../components/LeadDrawer.jsx';
import {
  Clock, Phone, MapPin, Users, Upload, UserPlus, Zap,
  Lock, History, RefreshCw, X,
} from 'lucide-react';
import toast from 'react-hot-toast';

const TABS = [
  { key: 'all',         label: 'All Available' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'mine',        label: 'My Locked' },
  { key: 'history',     label: 'My History' },
];

// PR #431: compact "6d 3h left" claim-countdown formatter.
function formatRemaining(unlocksAt) {
  const ms = new Date(unlocksAt).getTime() - Date.now();
  if (ms <= 0) return 'expiring';
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  if (days > 0) return `${days}d ${hours}h left`;
  const mins = Math.floor((ms % 3600000) / 60000);
  return hours > 0 ? `${hours}h ${mins}m left` : `${mins}m left`;
}

function LeadRow({ lead, currentAgentId, onClick, onClaim, claiming }) {
  const lockedAgent = lead.lockedTo?.agent;
  const lockedToOther = lockedAgent && String(lockedAgent._id || lockedAgent) !== String(currentAgentId);
  const lockedToMe = lockedAgent && String(lockedAgent._id || lockedAgent) === String(currentAgentId);
  const unlocksAt = lead.lockedTo?.unlocksAt ? new Date(lead.lockedTo.unlocksAt) : null;
  const claimReason = lead.lockedTo?.lockReason === 'claim';
  const lastDisp = lead.agedPoolHistory?.[lead.agedPoolHistory.length - 1]?.disposition;
  const poolTier = lead.agedPool?.poolTier;
  // Claimable: an untaken tier lead (A/B/C) not currently locked to anyone.
  const claimable = !lockedAgent && ['A', 'B', 'C'].includes(poolTier);
  // Sold/permanent: claimed by me with the timer voided (unlocksAt cleared).
  const permanent = lockedToMe && claimReason && !unlocksAt;

  return (
    <button
      onClick={onClick}
      className="w-full text-left panel p-4 flex items-center gap-3 hover:border-civix-purple/40 transition"
    >
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-base truncate">
          {lead.firstName} {lead.lastName}
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-white/50 flex-wrap">
          {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>}
          {lead.state && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{lead.state}</span>}
          {poolTier && <span>· Tier {poolTier}</span>}
          {lead.agedPool?.leadType && <span>· {lead.agedPool.leadType}</span>}
          {lead.agedPool?.vendorNotation && <span>· {lead.agedPool.vendorNotation}</span>}
          {lastDisp && <span>· last: {lastDisp}</span>}
        </div>
      </div>
      {lockedToMe && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
          <Lock className="w-3 h-3" />
          {permanent ? 'Yours · permanent' : 'In my funnel'}
          {claimReason && unlocksAt && <span className="opacity-80 ml-1">· {formatRemaining(unlocksAt)}</span>}
          {!claimReason && unlocksAt && <span className="opacity-80 ml-1">· unlocks {unlocksAt.toLocaleDateString()}</span>}
        </span>
      )}
      {lockedToOther && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
          <Lock className="w-3 h-3" /> Working with {lockedAgent.name || 'agent'}
          {unlocksAt && <span className="opacity-80 ml-1">· {unlocksAt.toLocaleDateString()}</span>}
        </span>
      )}
      {claimable && onClaim && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); if (!claiming) onClaim(lead._id); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); if (!claiming) onClaim(lead._id); } }}
          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border transition ${
            claiming
              ? 'opacity-50 cursor-wait bg-civix-purple/20 text-white/60 border-civix-purple/30'
              : 'bg-civix-purple text-white border-civix-purple hover:bg-civix-purple/80 cursor-pointer'
          }`}
        >
          <Lock className="w-3 h-3" /> {claiming ? 'Claiming…' : 'Claim'}
        </span>
      )}
    </button>
  );
}

function InviteModal({ open, onClose }) {
  const { agent } = useAuth();
  const [selectedEmail, setSelectedEmail] = useState('');
  const qc = useQueryClient();

  // Existing tenant agents (GET /api/agents already hides superadmins and is
  // admin-callable). Enrolling is by email — inviteTeammate() matches an
  // existing agent by email and enrolls them instantly, so no backend change.
  const agentsQuery = useQuery({
    queryKey: ['agents'],
    queryFn: () => apiGet('/agents'),
    enabled: open,
  });
  const options = (agentsQuery.data?.agents || []).filter(
    (a) => a.email && String(a._id) !== String(agent?._id),
  );
  const selected = options.find((a) => a.email === selectedEmail) || null;

  const sendInvite = useMutation({
    mutationFn: () => apiPost('/aged-pool/invite', { email: selectedEmail, name: selected?.name || '' }),
    onSuccess: (data) => {
      toast.success(data.kind === 'enrolled' ? 'Existing agent enrolled in pool' : 'Invite sent');
      qc.invalidateQueries({ queryKey: ['aged-pool-invites'] });
      setSelectedEmail('');
      onClose();
    },
    onError: (e) => toast.error(e.message || 'Invite failed'),
  });
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="panel p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-bold text-lg">Invite Teammate</div>
          <button onClick={onClose}><X className="w-5 h-5 text-white/60" /></button>
        </div>
        <label className="block text-xs text-white/60 mb-1">Team member</label>
        {agentsQuery.isLoading ? (
          <div className="text-sm text-white/50 py-2 mb-4">Loading team…</div>
        ) : options.length === 0 ? (
          <div className="text-sm text-white/50 py-2 mb-4">
            No other team members yet. Add agents under Team first, then invite them here.
          </div>
        ) : (
          <select
            value={selectedEmail}
            onChange={(e) => setSelectedEmail(e.target.value)}
            className="w-full px-3 py-2 rounded-sm bg-civix-dark border border-civix-border mb-4"
          >
            <option value="">Select a teammate…</option>
            {options.map((a) => (
              <option key={a._id} value={a.email}>
                {a.name ? `${a.name} — ${a.email}` : a.email}
              </option>
            ))}
          </select>
        )}
        <button
          onClick={() => sendInvite.mutate()}
          disabled={!selectedEmail || sendInvite.isPending}
          className="btn-primary w-full"
        >
          {sendInvite.isPending ? 'Sending…' : 'Send Invite'}
        </button>
      </div>
    </div>
  );
}

function UploadModal({ open, onClose }) {
  const [file, setFile] = useState(null);
  const [leadType, setLeadType] = useState('');
  const [vendorNotation, setVendorNotation] = useState('');
  const qc = useQueryClient();
  const upload = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      form.append('file', file);
      form.append('leadType', leadType);
      form.append('vendorNotation', vendorNotation);
      const { data } = await api.post('/aged-pool/leads/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Uploaded ${data.uploaded} leads`);
      qc.invalidateQueries({ queryKey: ['aged-pool-leads'] });
      qc.invalidateQueries({ queryKey: ['aged-pool-stats'] });
      onClose();
    },
    onError: (e) => toast.error(e.message || 'Upload failed'),
  });
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="panel p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-bold text-lg">Upload Aged Leads CSV</div>
          <button onClick={onClose}><X className="w-5 h-5 text-white/60" /></button>
        </div>
        <p className="text-xs text-white/50 mb-3">
          Columns: <code>first_name</code>, <code>last_name</code>, <code>phone</code>, <code>state</code>, <code>email</code> (optional).
        </p>
        <input
          type="file" accept=".csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="w-full mb-3 text-sm"
        />
        <label className="block text-xs text-white/60 mb-1">Lead Type</label>
        <input
          value={leadType} onChange={(e) => setLeadType(e.target.value)}
          placeholder="e.g. Final Expense"
          className="w-full px-3 py-2 rounded-sm bg-civix-dark border border-civix-border mb-3"
        />
        <label className="block text-xs text-white/60 mb-1">Vendor Notation</label>
        <input
          value={vendorNotation} onChange={(e) => setVendorNotation(e.target.value)}
          placeholder="e.g. LeadHeroes Q4 batch"
          className="w-full px-3 py-2 rounded-sm bg-civix-dark border border-civix-border mb-4"
        />
        <button
          onClick={() => upload.mutate()}
          disabled={!file || upload.isPending}
          className="btn-primary w-full"
        >
          {upload.isPending ? 'Uploading…' : 'Upload Leads'}
        </button>
      </div>
    </div>
  );
}

function PowerDialModal({ open, onClose, onStart }) {
  const [lines, setLines] = useState(3);
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="panel p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-bold text-lg">Power Dial Pool</div>
          <button onClick={onClose}><X className="w-5 h-5 text-white/60" /></button>
        </div>
        <p className="text-sm text-white/70 mb-3">
          Manual softphone power-dialer. The system pulls eligible pool leads and rings them through your browser softphone — no AI screener. TCPA hours, DNC, and the 3-hour tenant cooldown are enforced server-side.
        </p>
        <label className="block text-xs text-white/60 mb-1">Parallel lines: <span className="text-white font-bold">{lines}</span></label>
        <input
          type="range" min={1} max={5} value={lines}
          onChange={(e) => setLines(Number(e.target.value))}
          className="w-full mb-4"
        />
        <button onClick={() => onStart(lines)} className="btn-primary w-full">
          Start Session
        </button>
      </div>
    </div>
  );
}

export default function AgedLeads() {
  const { agent } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [dialOpen, setDialOpen] = useState(false);
  const [drawerLeadId, setDrawerLeadId] = useState(null);

  const poolMeta = useQuery({
    queryKey: ['aged-pool-meta'],
    queryFn: () => apiGet('/aged-pool'),
  });

  const stats = useQuery({
    queryKey: ['aged-pool-stats'],
    queryFn: () => apiGet('/aged-pool/stats'),
    refetchInterval: 30000,
  });

  const leads = useQuery({
    queryKey: ['aged-pool-leads', tab],
    queryFn: () => apiGet(`/aged-pool/leads?tab=${tab}`),
    refetchInterval: 30000,
  });

  const enrolled = poolMeta.data?.enrolled;
  const canInvite = poolMeta.data?.canInvite;

  const startSession = useMutation({
    mutationFn: (parallelLines) => apiPost('/aged-pool/power-dial/start', { parallelLines }),
    onSuccess: (data) => {
      setDialOpen(false);
      navigate(`/aged-leads/power-dial/${data.sessionId}`);
    },
    onError: (e) => toast.error(e.message || 'Failed to start session'),
  });

  // PR #431: claim a tier lead into the agent's private funnel (7-day timer).
  const qc = useQueryClient();
  const claimLead = useMutation({
    mutationFn: (leadId) => apiPost(`/lead-pool/leads/${leadId}/claim`, {}),
    onSuccess: () => {
      toast.success('Lead claimed — added to your funnel for 7 days');
      qc.invalidateQueries({ queryKey: ['aged-pool-leads'] });
      qc.invalidateQueries({ queryKey: ['aged-pool-stats'] });
    },
    onError: (e) => toast.error(e.message || 'Could not claim lead'),
  });

  return (
    <div className="space-y-4">
      <TopBar title="Aged Lead Pool" subtitle="Shared aged leads — manual power dialer" />

      {/* Stats + actions */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4 text-sm text-white/70">
          <span><b className="text-white">{stats.data?.stats?.total ?? '—'}</b> in pool</span>
          <span><b className="text-white">{stats.data?.stats?.locked ?? '—'}</b> locked</span>
          <span><b className="text-white">{stats.data?.stats?.workedToday ?? '—'}</b> worked today</span>
        </div>
        <div className="flex items-center gap-2">
          {canInvite && (
            <button onClick={() => setInviteOpen(true)} className="btn-ghost flex items-center gap-1.5">
              <UserPlus className="w-4 h-4" /> Invite Teammate
            </button>
          )}
          {canInvite && (
            <button onClick={() => setUploadOpen(true)} className="btn-ghost flex items-center gap-1.5">
              <Upload className="w-4 h-4" /> Upload Leads
            </button>
          )}
          {enrolled && (
            <button onClick={() => setDialOpen(true)} className="btn-primary flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600">
              <Zap className="w-4 h-4" /> Power Dial Pool
            </button>
          )}
          <button onClick={() => { leads.refetch(); stats.refetch(); }} className="btn-ghost p-2"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Enrollment banner */}
      {poolMeta.data && !enrolled && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-purple-500/30 bg-purple-500/10 text-purple-300 text-sm">
          <Users className="w-4 h-4 shrink-0" />
          <span className="flex-1">You are not yet enrolled in the Aged Lead Pool. Ask your admin to enroll you.</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 rounded-lg bg-civix-panel border border-civix-border/60 w-fit">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
              tab === t.key ? 'bg-civix-purple text-white shadow-sm' : 'text-white/50 hover:text-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Lead list */}
      {leads.isLoading ? (
        <div className="text-center text-white/40 py-20">Loading…</div>
      ) : (leads.data?.leads || []).length === 0 ? (
        <div className="panel p-10 text-center">
          <History className="w-8 h-8 mx-auto mb-3 text-white/40" />
          <div className="font-display font-bold text-lg mb-2">No leads in this view</div>
          <p className="text-white/50 text-sm max-w-sm mx-auto">
            {tab === 'all'    && 'No claimable leads in the pool right now.'}
            {tab === 'in_progress' && 'No leads are currently locked to anyone.'}
            {tab === 'mine'   && 'You don\'t have any leads locked to you.'}
            {tab === 'history' && 'You haven\'t dispositioned any pool leads yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {(leads.data?.leads || []).map((l) => (
            <LeadRow
              key={l._id}
              lead={l}
              currentAgentId={agent?._id}
              onClick={() => setDrawerLeadId(l._id)}
              onClaim={(id) => claimLead.mutate(id)}
              claiming={claimLead.isPending && claimLead.variables === l._id}
            />
          ))}
        </div>
      )}

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
      <PowerDialModal
        open={dialOpen}
        onClose={() => setDialOpen(false)}
        onStart={(lines) => startSession.mutate(lines)}
      />

      {/* In-place lead detail — peeking at a pool lead must NOT navigate away
          from the pool. LeadDrawer renders as a right-side overlay and offers
          its own "View full detail" link to /leads/:id for the deep view. */}
      {drawerLeadId && (
        <LeadDrawer
          leadId={drawerLeadId}
          onClose={() => setDrawerLeadId(null)}
          onChange={() => { leads.refetch(); stats.refetch(); }}
        />
      )}
    </div>
  );
}
